import { useEffect, useRef, useState } from "react";

/**
 * Debounced server search for a type-ahead box, with the stale-response guard.
 *
 * <p>Why this is shared rather than written per picker: two pickers (company, product) each had a
 * prefetched page filtered in the browser — `size=200` over 7,902 companies and `size=2000` over
 * 11,979 published products. Whatever was not in that first page could not be found at all, and
 * the window was ordered by `createdAt,desc`, so what you COULD find was simply the newest ERP
 * rows. The owner hit both: unrecognisable company names on new-order (2026-10-05), and
 * "NMT PLUS PWM H 25/80-180 se ne nalazi kroz search" — that pump sits at position 11,506 of
 * 11,979, so no cap short of the whole catalogue would have reached it.
 *
 * <p>The sequence number is not defensive padding. Without it a slow request for "nmt" resolving
 * after a fast one for "nmt plus pwm h 25/80-180" replaces the precise answer with the vague one,
 * which looks exactly like the truncation bug it replaced.
 *
 * @param query what the user typed; blank means "no search", and results go back to null
 * @param search the request. Must be referentially stable (useCallback) or the timer restarts on
 *     every render and nothing is ever sent.
 */
export function useRemoteSearch<T>(
  query: string,
  search: ((query: string) => Promise<T[]>) | undefined,
  delayMs = 250,
): { results: T[] | null; searching: boolean } {
  const [results, setResults] = useState<T[] | null>(null);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!search) return;
    const q = query.trim();
    if (!q) { setResults(null); setSearching(false); return; }
    const mine = ++seq.current;
    setSearching(true);
    const timer = setTimeout(() => {
      void search(q)
        .then((rows) => { if (seq.current === mine) { setResults(rows); setSearching(false); } })
        .catch(() => { if (seq.current === mine) { setResults([]); setSearching(false); } });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [query, search, delayMs]);

  return { results, searching };
}
